import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import { useMenu } from '@hooks/useMenu';
import useMessage from '@hooks/useMessage';
import { useModalWindow } from '@hooks/useModalWindow';
import { EnumSortDirection } from '@hooks/useRequest';
import ILinha from '@model/Linha';
import { useContratoOperadoraCombo } from '@model/ContratoOperadora';
import { EnumEmpresa, useEmpresaCombo } from '@model/Empresa';
import { EnumOperadora, useOperadoraCombo } from '@model/Operadora';
import { ILinhaFilter, useLinhaService } from '@services/LinhaService';
import React from 'react';
import { LinhaEditarModal } from './LinhaEditarModal';
import { LinhaFilter } from './LinhaFilter';
import { LinhaMenu } from './LinhaMenu';
import { LinhaTable } from './LinhaTable';
import { AtividadeModal } from '@mod/Atividade/AtividadeModal';
import { useRemetenteWhatsappCombo } from '@model/RemetenteWhatsapp';

export type LinhaProps = {
}

export function Linha (props: LinhaProps) {
    const data = useData<ILinha, ILinhaFilter>({
        filter: {
            ddd: useField(),
            numeroSemMascara: useField(),
            empresaId: useField(EnumEmpresa.None),
            operadoraId: useField(EnumOperadora.None),
            linhaEmpresa: useField(),
            numeroContrato: useField(),
            whatsappEnvio: useField(),
            disponibilidade: useField()
        },

        sortField: {
            name: "numeroSemMascara",
            direction: EnumSortDirection.Asc
        }
    })

    const loading = useLoading();
    const message = useMessage();
    const linhaService = useLinhaService();

    const contratoOperadoraCombo = useContratoOperadoraCombo();
    const empresaCombo = useEmpresaCombo();
    const operadoraCombo = useOperadoraCombo();
    const remetenteWhatsappCombo = useRemetenteWhatsappCombo();

    const menu = useMenu();
    const editarModal = useModalWindow();
    const atividadeModal = useModalWindow();

    React.useEffect(() => {
        if (!loading.isLoading) {
            loading.start();
            linhaService.listar(data.requestedData)
                .then(data.handleResultResponse)
                .catch(message.showException)
                .finally(loading.stop);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    useEffectOnce(() => {
        empresaCombo.loadOptions();
        operadoraCombo.loadOptions();
        contratoOperadoraCombo.loadOptions();
        remetenteWhatsappCombo.loadOptions();
    });

    return (
        <React.Fragment>
            <Main>
                <PageTitle>
                    <PageTitleText text="Linhas" />
                </PageTitle>

                <PageContent>
                    <LinhaFilter
                        filter={data.filter}
                        isLoading={loading.isLoading}
                        empresaCombo={empresaCombo}
                        operadoraCombo={operadoraCombo}
                        remetenteWhatsappCombo={remetenteWhatsappCombo}
                    />
                    
                    <LinhaTable 
                        data={data}
                        isLoading={loading.isLoading}
                        menu={menu}
                        contratoOperadoraCombo={contratoOperadoraCombo}
                    />
                </PageContent>
            </Main>


            {
                data.focusedRow && (
                    <React.Fragment>
                        <LinhaMenu
                            menu={menu}
                            linha={data.focusedRow}
                            editarModal={editarModal}
                            atividadeModal={atividadeModal}
                        />

                        <LinhaEditarModal 
                            modal={editarModal}
                            linha={data.focusedRow}
                            contratoOperadoraCombo={contratoOperadoraCombo}
                            onLinhaEditada={data.handleUpdateFocusedRow}
                        />

                        <AtividadeModal 
                            modal={atividadeModal}
                            entidade='linha'
                            chaves={{
                                linhaId: data.focusedRow.linhaId
                            }}
                        />
                    </React.Fragment>
                )
            }
        </React.Fragment>
    )
}